import { Radio } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  calculatePreviewLayout,
  createPreviewDocument,
  PreviewMode,
} from "./emailThumbnail.preview";

type Props = {
  fragment: string;
  width: number;
  height: number;
};

const MODES: Array<{ value: PreviewMode; label: string }> = [
  { value: "modern", label: "Modern client" },
  { value: "narrow", label: "Narrow container" },
  { value: "images-disabled", label: "Images disabled" },
  { value: "outlook", label: "Outlook approximation" },
];

export const EmailThumbnailPreview = ({ fragment, width, height }: Props) => {
  const [mode, setMode] = useState<PreviewMode>("modern");
  const [frameWidth, setFrameWidth] = useState<number | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const layout = useMemo(
    () =>
      calculatePreviewLayout({
        mode,
        sourceWidth: width,
        sourceHeight: height,
        frameWidth: frameWidth ?? 0,
      }),
    [frameWidth, height, mode, width]
  );
  const source = useMemo(
    () =>
      createPreviewDocument(fragment, mode, {
        sourceWidth: width,
        sourceHeight: height,
      }),
    [fragment, height, mode, width]
  );

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const applyWidth = (nextWidth: number) => {
      if (!Number.isFinite(nextWidth) || nextWidth <= 0) return;
      setFrameWidth((current) =>
        current !== null && Math.abs(current - nextWidth) < 0.5
          ? current
          : nextWidth
      );
    };
    const measure = () => {
      applyWidth(frame.clientWidth || frame.getBoundingClientRect().width);
    };

    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver((entries) => {
        applyWidth(entries[0]?.contentRect.width || frame.clientWidth);
      });
      observer.observe(frame);
      return () => observer.disconnect();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
  }, []);

  return (
    <section
      className="email-thumbnail-preview"
      aria-labelledby="email-thumbnail-preview-title"
    >
      <div className="email-thumbnail-section-heading">
        <div>
          <h2 id="email-thumbnail-preview-title">Email preview</h2>
          <p>Sample values are substituted only in this sandboxed preview.</p>
        </div>
      </div>
      <Radio.Group
        className="email-thumbnail-preview-modes"
        value={mode}
        onChange={(event) => setMode(event.target.value)}
        optionType="button"
        buttonStyle="solid"
        aria-label="Email client preview mode"
      >
        {MODES.map((item) => (
          <Radio.Button key={item.value} value={item.value}>
            {item.label}
          </Radio.Button>
        ))}
      </Radio.Group>
      {mode === "outlook" && (
        <p className="email-thumbnail-mode-note" role="note">
          Approximation only: this view emphasizes fixed dimensions and classic
          Outlook spacing. The generated source contains the actual VML
          fallback.
        </p>
      )}
      {mode === "images-disabled" && (
        <p className="email-thumbnail-mode-note" role="note">
          Background image requests are removed so the fallback color and
          accessible link title can be reviewed.
        </p>
      )}
      <div
        ref={frameRef}
        className={`email-thumbnail-preview-frame is-${mode}`}
      >
        <div
          className="email-thumbnail-preview-scale-reserve"
          style={{
            width: `${layout.renderedWidth}px`,
            height: `${layout.renderedHeight}px`,
          }}
        >
          <div
            className="email-thumbnail-preview-scale-layer"
            style={{
              width: `${layout.viewportWidth}px`,
              height: `${layout.viewportHeight}px`,
              transform: `scale(${layout.scale})`,
              transformOrigin: "top left",
            }}
          >
            <iframe
              title={`${
                MODES.find((item) => item.value === mode)?.label
              } email thumbnail preview`}
              sandbox=""
              srcDoc={source}
              style={{
                width: `${layout.viewportWidth}px`,
                height: `${layout.viewportHeight}px`,
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
